/**
 * Registro / entrar — ver `MEMBERSHIP_ROUTE.REGISTRO` y `MEMBERSHIP_ROUTE.ENTRAR`
 *
 * Post-pago: cuenta → `MEMBERSHIP_ROUTE.COMPLETAR_REGISTRO` → pendiente → mi acceso.
 * Rutas canónicas y portal: `membershipAccessFlow.ts`.
 */

import { MembershipPortalHint } from "@/components/membership/MembershipPortalHint";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useSEO } from "@/hooks/useSEO";
import { getRegistroClerkAppearance } from "@/lib/clerkRegistroAppearance";
import { SignIn, SignUp, useAuth } from "@clerk/clerk-react";
import { Loader2, LogIn, ShieldCheck, Sparkles, UserPlus } from "lucide-react";
import { useMemo } from "react";
import { Navigate, useLocation, useSearchParams } from "react-router-dom";
import { BRAND_PERSON_NAME, BRAND_SITE_URL } from "@/lib/brandConfig";
import {
  MEMBERSHIP_ROUTE,
  MEMBERSHIP_ROUTE_EN_COMPLETE_PROFILE,
} from "@/lib/membershipAccessFlow";

const SEO = {
  title: "Completá tu registro",
  description: `Registrate para acceder a tu membresía en ${BRAND_PERSON_NAME}.`,
  keywords: "",
  ogImage: `${BRAND_SITE_URL}/og.png`,
  canonical: `${BRAND_SITE_URL}${MEMBERSHIP_ROUTE.REGISTRO}`,
  robots: "noindex, nofollow" as const,
};

interface RegistroProps {
  /** Si "sign-in", muestra ingreso en lugar de signup */
  mode?: "sign-up" | "sign-in";
}

export default function Registro({ mode = "sign-up" }: RegistroProps) {
  useSEO(SEO);
  const { search } = useLocation();
  const [params] = useSearchParams();
  const { isLoaded, isSignedIn } = useAuth();

  const clerkAppearance = useMemo(() => getRegistroClerkAppearance(), []);

  // Preservar query params críticos para que /completar-registro los reciba
  const sessionId = params.get("session_id");
  const subscriptionId = params.get("subscription_id");
  const fromVip = params.get("from_special_offer") === "1" || params.get("from_vip") === "1";
  const planTier = (params.get("plan_tier") || "").trim().toLowerCase();
  const fromBasic = params.get("from_basic") === "1" || planTier === "basic";
  const fromStandard =
    params.get("from_standard") === "1" || (planTier === "standard" && !fromVip && !fromBasic);
  const fromMp = params.get("from_mp") === "true";
  /** Solicitud de acceso SIN pago (dual template VIP) → no afirmar "pago recibido". */
  const isAccessRequest = params.get("access_request") === "1";
  /** Free-join DualCTA: tras perfil → link grupo free. */
  const isFreeJoin = params.get("free_join") === "1" || params.get("plan_tier") === "free";
  /** Canal WhatsApp Broadcast — registro previo al pago, el equipo envía el link de MP. */
  const fromWhatsApp = params.get("from_whatsapp") === "1";

  /**
   * Idioma del visitante: `?lang=en` o `?language=en` activan el embudo EN
   * (`/portal/complete-profile`). El resto sigue en ES (`/portal/completar-perfil`).
   * Preservamos el param en la query para que el siguiente paso también lo respete.
   */
  const langParam = (params.get("lang") || params.get("language") || "")
    .trim()
    .toLowerCase();
  const isEnglish = langParam === "en" || langParam.startsWith("en-");
  const completarPerfilPath = isEnglish
    ? MEMBERSHIP_ROUTE_EN_COMPLETE_PROFILE
    : MEMBERSHIP_ROUTE.COMPLETAR_REGISTRO;

  const queryParts: string[] = [];
  if (fromVip) queryParts.push("from_special_offer=1");
  if (fromBasic) queryParts.push("from_basic=1");
  if (fromStandard) queryParts.push("from_standard=1");
  if (planTier) queryParts.push(`plan_tier=${encodeURIComponent(planTier)}`);
  if (fromMp) queryParts.push("from_mp=true");
  if (isAccessRequest) queryParts.push("access_request=1");
  if (isFreeJoin) {
    queryParts.push("free_join=1");
    if (!planTier) queryParts.push("plan_tier=free");
  }
  if (fromWhatsApp) queryParts.push("from_whatsapp=1");
  if (sessionId) queryParts.push(`session_id=${encodeURIComponent(sessionId)}`);
  if (subscriptionId) queryParts.push(`subscription_id=${encodeURIComponent(subscriptionId)}`);
  if (langParam) queryParts.push(`lang=${encodeURIComponent(langParam)}`);
  const countryParam = (params.get("country") || "").trim().toUpperCase().slice(0, 2);
  if (/^[A-Z]{2}$/.test(countryParam)) {
    queryParts.push(`country=${encodeURIComponent(countryParam)}`);
  }
  const queryWithLang = queryParts.length ? `?${queryParts.join("&")}` : "";

  /** Tras iniciar sesión: misma corrida query que signup salvo ?next absoluto permitido */
  const rawNext = params.get("next")?.trim() ?? "";
  const redirectAfterSignIn =
    rawNext.startsWith("/") && !rawNext.startsWith("//")
      ? rawNext
      : `${completarPerfilPath}${queryWithLang}`;

  const forceRedirectUrl = `${completarPerfilPath}${queryWithLang}`;

  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
      </div>
    );
  }

  if (isSignedIn) {
    // Si ya logueado, ir directo al completar registro (con los mismos query params)
    return <Navigate to={forceRedirectUrl} replace />;
  }

  const isSignUp = mode === "sign-up";

  // ── Variante minimal para solicitudes de acceso gratis (access_request=1) ──
  // Mobile-first: sin card, sin títulos ni hint propios. El widget de Clerk ya
  // trae el logo de la app + "Crea tu cuenta / para continuar en {marca}" —
  // no duplicar branding acá.
  if (isAccessRequest && isSignUp) {
    return (
      <div className="flex min-h-[100dvh] flex-col items-center justify-start bg-zinc-950 px-3 pb-8 pt-6 text-zinc-100 sm:justify-center sm:px-4 sm:py-10">
        <div className="w-full max-w-md">
          <SignUp
            appearance={clerkAppearance}
            routing="hash"
            forceRedirectUrl={forceRedirectUrl}
            fallbackRedirectUrl={forceRedirectUrl}
            signInUrl={search ? `${MEMBERSHIP_ROUTE.ENTRAR}${search}` : MEMBERSHIP_ROUTE.ENTRAR}
            signInForceRedirectUrl={redirectAfterSignIn}
            signInFallbackRedirectUrl={redirectAfterSignIn}
          />
        </div>
      </div>
    );
  }

  const title = isSignUp ? "Creá tu cuenta" : "Volvé a entrar";
  const subtitle = isSignUp
    ? isAccessRequest
      ? `Creá tu cuenta para solicitar acceso VIP a ${BRAND_PERSON_NAME}. El equipo revisará tu solicitud.`
      : fromWhatsApp
      ? `Registrá tu cuenta para unirte al canal de WhatsApp de ${BRAND_PERSON_NAME}. En breve te enviamos el link de pago.`
      : fromVip
      ? `¡Gracias por tu compra! Seguí con tu membresía en ${BRAND_PERSON_NAME}: completá tu registro.`
      : fromBasic
        ? `¡Gracias por tu compra! Para activar tu acceso al grupo de ${BRAND_PERSON_NAME}, creá tu cuenta y completá el perfil.`
        : fromStandard
          ? `¡Gracias por tu compra! Creá tu cuenta para activar tu membresía Standard en ${BRAND_PERSON_NAME}.`
          : `Creá una cuenta para acceder a tu membresía en ${BRAND_PERSON_NAME}.`
    : `Iniciá sesión para acceder a ${BRAND_PERSON_NAME}.`;

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center px-4 py-8">
      <div className="mx-auto max-w-lg w-full">
        <Card className="border-white/10 bg-zinc-900/80 backdrop-blur shadow-2xl shadow-black/40">
          <CardHeader className="text-center space-y-3 pb-4">
            <div className="mx-auto w-14 h-14 rounded-full bg-amber-500/20 flex items-center justify-center">
              {isSignUp ? (
                <UserPlus className="w-7 h-7 text-amber-300" />
              ) : (
                <LogIn className="w-7 h-7 text-amber-300" />
              )}
            </div>
            <CardTitle className="text-xl sm:text-2xl text-zinc-100">{title}</CardTitle>
            <CardDescription className="text-sm text-zinc-400">{subtitle}</CardDescription>
          </CardHeader>

          <CardContent className="space-y-4 pt-2">
            {isAccessRequest ? (
              <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 px-4 py-3 text-xs text-amber-100 flex items-start gap-2">
                <Sparkles className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
                <p>
                  <strong className="text-amber-200">Después de crear tu cuenta</strong> el equipo revisará
                  tu solicitud de acceso VIP y, si corresponde, te asignará el link privado (puede tardar
                  unas horas según el horario).
                </p>
              </div>
            ) : fromWhatsApp ? (
              <div className="rounded-lg bg-green-500/10 border border-green-500/30 px-4 py-3 text-xs text-green-100 flex items-start gap-2">
                <Sparkles className="w-4 h-4 text-green-300 shrink-0 mt-0.5" />
                <p>
                  <strong className="text-green-200">Después de crear tu cuenta</strong> te enviamos el link de MercadoPago
                  para completar tu suscripción al canal de WhatsApp (puede tardar unas horas según el horario).
                </p>
              </div>
            ) : (fromVip || fromBasic || fromStandard) && (
              <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 px-4 py-3 text-xs text-amber-100 flex items-start gap-2">
                <Sparkles className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
                <p>
                  Tu pago fue recibido. <strong className="text-amber-200">Después de crear tu cuenta</strong> el equipo
                  {fromBasic || fromStandard
                    ? " validará tu cuenta y te enviará el link de ingreso correspondiente cuando corresponda."
                    : " te asignará el link privado de Telegram (puede tardar unas horas según el horario)."}
                </p>
              </div>
            )}

            <div className="rounded-xl bg-black/25 border border-white/10 px-3 py-4 sm:px-4">
              {isSignUp ? (
                <SignUp
                  appearance={clerkAppearance}
                  routing="hash"
                  forceRedirectUrl={forceRedirectUrl}
                  fallbackRedirectUrl={forceRedirectUrl}
                  signInUrl={search ? `${MEMBERSHIP_ROUTE.ENTRAR}${search}` : MEMBERSHIP_ROUTE.ENTRAR}
                  signInForceRedirectUrl={redirectAfterSignIn}
                  signInFallbackRedirectUrl={redirectAfterSignIn}
                />
              ) : (
                <SignIn
                  appearance={clerkAppearance}
                  routing="hash"
                  forceRedirectUrl={redirectAfterSignIn}
                  fallbackRedirectUrl={redirectAfterSignIn}
                  signUpUrl={search ? `${MEMBERSHIP_ROUTE.REGISTRO}${search}` : MEMBERSHIP_ROUTE.REGISTRO}
                  signUpForceRedirectUrl={forceRedirectUrl}
                  signUpFallbackRedirectUrl={forceRedirectUrl}
                />
              )}
            </div>

            <div className="pt-2 border-t border-white/10 flex items-center gap-2 text-xs text-zinc-500">
              <ShieldCheck className="w-4 h-4 shrink-0" />
              <span>El ingreso al grupo/link privado requiere una aprobación manual del equipo.</span>
            </div>

            <MembershipPortalHint className="pt-3 border-t border-white/10" />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
